import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, CircularProgress } from '@mui/material';
import Dialog from '../../../../components/Dialog/Dialog';
import TruckDataGrid from './components/TruckDataGrid';
import CalculationsSummary from './components/CalculationsSummary';
import ReferenceTablesInfo from './components/ReferenceTablesInfo';
import { EsalCalculationModalProps, TruckData, SummaryCalculationData, DEFAULT_TRUCK_CONFIGURATIONS, DEFAULT_TRUCK_VALUES, ESAL_CONSTANTS, CalculationsSummaryInputs } from './types/ESALTypes';
import { ESALFormulas } from './ESALFormulas';
import { useInvalidDecimalKeys } from '../../../hooks/useInvalidDecimalKeys';
import { useAppDispatch, useAppSelector } from '../../../../reduxService/hooks';
import { selectEsalTrucks, selectEsalSummaryInputs, setTrucks, updateTruck, setSummaryInputs, resetEsalState } from '../../store/project/esalSlice';
import { selectDynamicProject } from '../../store/project/projectslice';
import { logDebug, logError } from '../../../utils/utils';

// Unused style injection functions removed - dead code

/**
 * Modal component for comprehensive ESAL calculation.
 *
 * Features:
 * - Support for 8 truck types with real-time ESAL calculations
 * - Single table showing both directions for each truck
 * - Inline editing with Material-UI Table
 * - Summary calculation panel with traffic analysis
 * - Excel-based formula calculations (EALF, growth factors, etc.)
 */
const EsalCalculationModal: React.FC<EsalCalculationModalProps> = ({ open, onClose, onCalculationComplete }) => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();
    const trucks = useAppSelector(selectEsalTrucks);
    const summaryInputs = useAppSelector(selectEsalSummaryInputs);
    const projectData = useAppSelector(selectDynamicProject);
    const [isCalculating, setIsCalculating] = useState<boolean>(false);
    const hasInitialized = useRef(false);
    const { hasInvalidKeys: hasInvalidDecimals, onValidityChange: onDecimalValidityChange, resetKey: decimalResetKey, reset: resetInvalidDecimals } = useInvalidDecimalKeys();

    // The cells are remounted on reopen: start again from a clean set
    const [seenOpen, setSeenOpen] = useState(open);
    if (open !== seenOpen) {
        setSeenOpen(open);
        if (open) resetInvalidDecimals();
    }

    /**
     * Inject global styles for this modal only
     */
    useEffect(() => {
        const styleId = 'esal-modal-styles';

        if (open) {
            // Remove existing style if any
            const existingStyle = document.getElementById(styleId);
            if (existingStyle) {
                document.head.removeChild(existingStyle);
            }

            // Add new style targeting our specific dialog class
            const style = document.createElement('style');
            style.id = styleId;
            style.innerHTML = `
                .esal-calculation-dialog .MuiDialog-paper {
                    width: 2000px !important;
                    max-width: 98vw !important;
                    margin: 16px !important;
                }
                .esal-calculation-dialog .MuiDialog-container {
                    align-items: flex-start !important;
                }
            `;
            document.head.appendChild(style);
        }

        // Cleanup when modal closes or component unmounts
        return () => {
            const style = document.getElementById(styleId);
            if (style) {
                document.head.removeChild(style);
            }
        };
    }, [open]);

    /**
     * Calculate truck ESAL with appropriate method based on available data
     * IMPORTANT: Always use AASHTO standard SN to match Excel calculations exactly
     */
    const calculateTruckESAL = useCallback(
        (truck: TruckData): TruckData => {
            // Always use AASHTO standard SN to match Excel reference calculations exactly
            // This ensures the modal shows the same values as the Excel sheet (Q10=592.99, Q14=336.86, etc.)
            return ESALFormulas.updateTruckESAL(truck, true, ESALFormulas.CONSTANTS.STRUCTURAL_NUMBER); // useDynamic=true, structuralNumber=5.0
        },
        [] // Remove projectData dependency to ensure consistent calculation
    );

    /**
     * Reconstructs full TruckData[] from saved minimal data
     * Merges saved user values with static configuration from defaults
     */
    const reconstructTrucksFromSaved = useCallback(
        (
            savedTrucks: Array<{
                id: number;
                selected: boolean;
                direction1Vehicles: number;
                direction2Vehicles: number;
                passagePercentage: number;
                customTotalLoad?: number;
            }>
        ): TruckData[] => {
            return DEFAULT_TRUCK_CONFIGURATIONS.map(config => {
                const saved = savedTrucks.find(s => s.id === config.id);

                if (!saved) {
                    // Truck not in saved data, use defaults
                    const initialTruck: TruckData = {
                        id: config.id,
                        selected: false,
                        name: config.name,
                        axles: config.axles,
                        designTotalLoad: config.designTotalLoad,
                        customTotalLoad: 0,
                        direction1Vehicles: 0,
                        direction2Vehicles: 0,
                        esalPerDay: 0,
                        passagePercentage: 0,
                        imageSrc: config.imageSrc,
                    };
                    return calculateTruckESAL(initialTruck);
                }

                // Merge saved data with default configuration
                const truck: TruckData = {
                    id: config.id,
                    selected: saved.selected,
                    name: config.name,
                    axles: config.axles,
                    designTotalLoad: config.designTotalLoad,
                    customTotalLoad: saved.customTotalLoad ?? 0,
                    direction1Vehicles: saved.direction1Vehicles,
                    direction2Vehicles: saved.direction2Vehicles,
                    esalPerDay: 0, // Will be recalculated
                    passagePercentage: saved.passagePercentage,
                    imageSrc: config.imageSrc,
                };

                // Recalculate ESAL with saved configuration
                return calculateTruckESAL(truck);
            });
        },
        [calculateTruckESAL]
    );

    /**
     * Initialize truck data with default configurations and Excel values
     */
    const initializeTruckData = useCallback((): TruckData[] => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return DEFAULT_TRUCK_CONFIGURATIONS.map((config: any) => {
            // Find corresponding default values from Excel data
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const defaultValues = DEFAULT_TRUCK_VALUES.find((value: any) => value.id === config.id);

            const initialTruck: TruckData = {
                id: config.id,
                selected: defaultValues?.selected ?? false,
                name: config.name,
                axles: config.axles,
                designTotalLoad: config.designTotalLoad,
                customTotalLoad: defaultValues?.customTotalLoad ?? 0,
                direction1Vehicles: defaultValues?.direction1Vehicles ?? 0,
                direction2Vehicles: defaultValues?.direction2Vehicles ?? 0,
                esalPerDay: 0,
                passagePercentage: defaultValues?.passagePercentage ?? 0,
                imageSrc: config.imageSrc,
            };

            // Calculate initial ESAL if truck is selected and has vehicles
            return calculateTruckESAL(initialTruck);
        });
    }, [calculateTruckESAL]);

    /**
     * Initialize summary calculation data using Redux summaryInputs for key fields
     */
    const summaryData: SummaryCalculationData = useMemo(() => {
        const totalPassages = trucks.reduce((sum, truck) => {
            if (truck.selected) {
                return sum + truck.direction1Vehicles + truck.direction2Vehicles;
            }
            return sum;
        }, 0);

        // Use Redux summaryInputs for these fields
        const directionalDistribution = summaryInputs.dd;
        const laneDistribution = summaryInputs.dl;
        const analysisPeriod = summaryInputs.analysisPeriod;
        const yearGrowthRate = summaryInputs.growthRate;

        // Calculate compound traffic growth factor using Redux values
        const compoundTrafficGrowthFactor = ESALFormulas.calculateTrafficGrowthFactor(yearGrowthRate, analysisPeriod);

        const totalVolumeTraffic = totalPassages * analysisPeriod * 365;
        const trafficDesignLane = totalVolumeTraffic * directionalDistribution * laneDistribution;

        return {
            totalPassages,
            directionalDistribution,
            laneDistribution,
            analysisPeriod,
            yearGrowthRate,
            compoundTrafficGrowthFactor,
            totalVolumeTraffic,
            trafficDesignLane,
            userDefinedTrafficDesignLane: 0,
        };
    }, [trucks, summaryInputs]);

    /**
     * Returns the initial summary inputs with correct number types (not literals)
     */
    const getInitialSummaryInputs = useCallback(
        (): CalculationsSummaryInputs => ({
            dd: Number(ESAL_CONSTANTS.DEFAULT_DD),
            dl: Number(ESAL_CONSTANTS.DEFAULT_DL),
            analysisPeriod: Number(ESAL_CONSTANTS.DEFAULT_ANALYSIS_PERIOD),
            growthRate: Number(ESAL_CONSTANTS.DEFAULT_GROWTH_RATE),
            trafficDesignLane: 0,
        }),
        []
    );

    /**
     * Calculate total ESAL from all selected trucks
     */
    const totalESAL = useMemo(() => {
        return trucks.filter(truck => truck.selected).reduce((total, truck) => total + truck.esalPerDay, 0);
    }, [trucks]);

    /**
     * Initialize truck data when modal opens
     */

    useEffect(() => {
        // Initialize modal state when opened
        if (open && !hasInitialized.current && trucks.length === 0) {
            // FLAT structure: trafficLoad is directly on projectInfo.data
            const projectInfoData = projectData.data?.projectInfo?.data;

            // Access trafficLoad directly from FLAT structure
            const trafficLoad = (projectInfoData as any)?.trafficLoad;

            const savedTrucks = trafficLoad?.esalTrucks;
            const savedSummaryInputs = trafficLoad?.esalSummaryInputs;

            try {
                if (savedTrucks && savedSummaryInputs) {
                    // Restore from saved data
                    const reconstructed = reconstructTrucksFromSaved(savedTrucks);
                    dispatch(setTrucks(reconstructed));
                    // Merge saved inputs with default trafficDesignLane (calculated value)
                    dispatch(
                        setSummaryInputs({
                            ...savedSummaryInputs,
                            trafficDesignLane: 0, // Will be recalculated
                        })
                    );
                    logDebug('EsalCalculationModal', 'Loaded saved ESAL configuration', {
                        truckCount: savedTrucks.length,
                        selectedCount: savedTrucks.filter((t: any) => t.selected).length,
                        summaryInputs: savedSummaryInputs,
                    });
                } else {
                    // Initialize with defaults (new project or old project)
                    dispatch(setTrucks(initializeTruckData()));
                    dispatch(setSummaryInputs(getInitialSummaryInputs()));
                    logDebug('EsalCalculationModal', 'Initialized ESAL with defaults');
                }
            } catch (error) {
                logError('EsalCalculationModal', 'Failed to load saved ESAL data, using defaults', error);
                dispatch(setTrucks(initializeTruckData()));
                dispatch(setSummaryInputs(getInitialSummaryInputs()));
            }

            hasInitialized.current = true;
        }
        if (!open) {
            hasInitialized.current = false;
        }
    }, [open, trucks.length, dispatch, projectData, reconstructTrucksFromSaved, initializeTruckData, getInitialSummaryInputs]);

    /**
     * Handle truck data updates from grid components
     */
    const handleTruckUpdate = useCallback(
        (updatedTruck: TruckData) => {
            dispatch(updateTruck(updatedTruck));
        },
        [dispatch]
    );

    /**
     * Handle Run calculation
     */
    const handleRun = useCallback(async () => {
        setIsCalculating(true);

        try {
            // Simulate calculation delay for UX
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Calculate trafficDesignLane using the same logic as CalculationsSummary
            // H84 = Q82*365 (Total passages per year)
            const totalPassagesPerYear = totalESAL * 365;

            // H89 = TGF = Traffic Growth Factor
            const tgf = ESALFormulas.calculateTrafficGrowthFactor(summaryInputs.growthRate, summaryInputs.analysisPeriod);

            // H90 = w'18 = TGF * N1 (using totalPassagesPerYear as N1)
            const totalVolumeTraffic = tgf * totalPassagesPerYear;

            // H91 = w18 = DD * DL * w'18 (Excel H91 = DD*DL*w'18)
            const currentTrafficDesignLane = summaryInputs.dd * summaryInputs.dl * totalVolumeTraffic;

            // Return both esalFinalValue and totalPassagesPerYear for canvas tables
            onCalculationComplete({
                esalFinalValue: Math.round(currentTrafficDesignLane),
                totalPassagesPerYear: Math.round(totalPassagesPerYear),
            });
            onClose();
        } catch (error) {
        } finally {
            setIsCalculating(false);
        }
    }, [summaryInputs.dd, summaryInputs.dl, summaryInputs.growthRate, summaryInputs.analysisPeriod, totalESAL, onCalculationComplete, onClose]);

    /**
     * Handle modal close with cleanup
     */
    const handleClose = useCallback(() => {
        setIsCalculating(false);
        resetInvalidDecimals();
        onClose();
    }, [onClose, resetInvalidDecimals]);

    /**
     * Check if calculation can be run (at least one truck selected)
     */
    const canRunCalculation = useMemo(() => {
        return trucks.some(truck => truck.selected && (truck.direction1Vehicles > 0 || truck.direction2Vehicles > 0));
    }, [trucks]);
    const canRun = canRunCalculation && !isCalculating && !hasInvalidDecimals;

    // Wrapper per evitare errori di tipo con setState
    const handleSummaryInputsChange = useCallback(
        (inputs: CalculationsSummaryInputs) => {
            dispatch(setSummaryInputs(inputs));
        },
        [dispatch]
    );

    /**
     * Handle reset to default values
     */
    const handleReset = useCallback(() => {
        dispatch(resetEsalState());
        dispatch(setTrucks(initializeTruckData()));
        dispatch(setSummaryInputs(getInitialSummaryInputs()));
        resetInvalidDecimals();
    }, [dispatch, initializeTruckData, getInitialSummaryInputs, resetInvalidDecimals]);

    return (
        <Dialog
            open={open}
            title={t('esal_automatic_calculation_title', 'Automatic ESAL Calculation') ?? 'Automatic ESAL Calculation'}
            onClose={handleClose}
            className="esal-calculation-dialog"
            actions={[
                {
                    text: t('cancel_button_label', 'Cancel'),
                    callback: handleClose,
                    color: 'secondary',
                },
                {
                    text: t('reset_button_label', 'Reset'),
                    callback: handleReset,
                    color: 'warning',
                },
                {
                    text: t('run_button_label', 'Run'),
                    callback: canRun ? handleRun : undefined,
                    disabled: !canRun,
                    color: 'primary',
                },
            ]}>
            <Box
                sx={{
                    p: 2,
                    minHeight: 800,
                    width: '100%', // Occupa tutta la larghezza disponibile
                    '.esal-calculation-modal.all-purpose-dialog .MuiDialog-paper': {
                        maxWidth: '98vw !important',
                        width: '2000px !important',
                        margin: 'auto',
                    },
                }}>
                {/* Main content layout: Table top, Reference tables, Summary bottom */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: 'calc(100% - 80px)' }}>
                    {/* Top panel: Truck data grid */}
                    <Box sx={{ flex: '1 1 auto', overflow: 'hidden' }}>
                        <TruckDataGrid
                            trucks={trucks}
                            onTruckUpdate={handleTruckUpdate}
                            isEditable={!isCalculating}
                            projectData={projectData}
                            onValidityChange={onDecimalValidityChange}
                            resetKey={decimalResetKey}
                        />
                    </Box>

                    {/* Middle panel: Reference tables for DL and Analysis Period */}
                    <Box sx={{ flexShrink: 0 }}>
                        <ReferenceTablesInfo />
                    </Box>

                    {/* Bottom panel: Calculations summary */}
                    <Box sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider', pt: 2 }}>
                        <CalculationsSummary
                            summaryData={summaryData}
                            totalESAL={totalESAL}
                            isCalculating={isCalculating}
                            onSummaryInputsChange={handleSummaryInputsChange}
                            onValidityChange={onDecimalValidityChange}
                            resetKey={decimalResetKey}
                        />
                    </Box>
                </Box>

                {/* Loading overlay */}
                {isCalculating && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1000,
                        }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <CircularProgress />
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {t('esal_calculating_message', 'Calculating ESAL values...')}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        </Dialog>
    );
};

export default EsalCalculationModal;
